import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Clock, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { trackId } from "@/lib/api";
import { Overline, cx } from "@/ui";
import PageSeo from "@/components/PageSeo";

const STAGE_LABELS = {
  access_confirmed: ["Access confirmed", "Acceso confirmado"],
  deposit_confirmed: ["Deposit confirmed", "Depósito confirmado"],
  specialist_assigned: ["Specialist assigned", "Especialista asignado"],
  service_in_progress: ["Service in progress", "Servicio en progreso"],
  quality_check: ["Quality check", "Control de calidad"],
  aircraft_ready: ["Aircraft ready", "Aeronave lista"],
  report_delivered: ["Report delivered", "Reporte entregado"],
};

export default function TrackStatus() {
  const { L, lang } = useLang();
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const REPORT_ITEMS = [
    ["Cabin vacuum & surfaces", "Aspirado de cabina y superficies"],
    ["Leather cleaned & conditioned", "Piel limpiada y acondicionada"],
    ["Windows / acrylics safe-clean", "Ventanas / acrílicos safe-clean"],
    ["Galley & lavatory reset", "Galley y baño reset"],
    ["Carpet spot treatment", "Tratamiento de mancha en alfombra"],
    ["Neutral odor finish", "Acabado de olor neutro"],
  ];

  const search = async (val) => {
    const q = (val ?? id).trim();
    if (!q) return;
    setLoading(true); setErr(""); setRes(null);
    try { setRes(await trackId(q)); } catch (e) { setErr(L("ID not found. Check and try again.", "ID no encontrado. Verifica e intenta de nuevo.")); }
    setLoading(false);
  };
  useEffect(() => { if (params.get("id")) search(params.get("id")); /* eslint-disable-next-line */ }, []);

  return (
    <div className="grain min-h-screen pt-32 pb-24">
      <PageSeo esPath="/track" enPath="/en/track" title={L("Track Aircraft Service Status | dukk", "Seguimiento de Servicio de Aeronave | dukk")} description={L("Track the readiness status of your aircraft service with dukk — enter your request ID or service ticket to see live progress.", "Consulta el estado de readiness de tu servicio de aeronave con dukk — ingresa tu ID de solicitud o ticket de servicio para ver el progreso en vivo.")} />
      <div className="max-w-2xl mx-auto px-6">
        <Overline gold>{L("Track Status", "Seguimiento")}</Overline>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-4">{L("Where is my aircraft service?", "¿Dónde va mi servicio?")}</h1>
        <p className="text-white/45 font-light mt-3">{L("Enter your Request ID (TB-RQ / TB-PR / TB-MB) or Service Ticket (TB-####).", "Ingresa tu ID de solicitud (TB-RQ / TB-PR / TB-MB) o Ticket de Servicio (TB-####).")}</p>
        <div className="flex gap-3 mt-8">
          <input data-testid="track-input" value={id} onChange={(e) => setId(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="TB-RQ-1234" className="input-lux font-mono tracking-wider" />
          <button data-testid="track-search-btn" onClick={() => search()} className="shrink-0 bg-[#7DAFFF] text-[#050505] px-6 rounded-sm hover:opacity-90 hover:shadow-[0_0_16px_rgba(37,99,235,0.6)] transition-all flex items-center gap-2"><Search size={16} />{loading ? "…" : L("Track", "Buscar")}</button>
        </div>
        {err && <p data-testid="track-error" className="text-red-400/80 font-light mt-5">{err}</p>}

        {res?.type === "request" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/[0.05] p-7">
            <div className="flex items-center gap-3"><Clock className="text-[#F59E0B] animate-pulse" size={22} /><span className="font-mono text-2xl tracking-wider">{res.request.requestId}</span></div>
            <p className="text-[#F59E0B] font-mono text-xs tracking-widest uppercase mt-4">{L("Pending concierge validation", "Validación pendiente")}</p>
            <p data-testid="request-pending-message" className="text-white/60 font-light mt-3 leading-relaxed">{L("This request is pending concierge validation. A Service Ticket will be issued after access, scope and deposit are confirmed.", "Esta solicitud está pendiente de validación. Se emitirá un Ticket de Servicio tras confirmar acceso, alcance y depósito.")}</p>
            {res.request.finalQuote && <p className="text-white/70 mt-4 text-sm">{L("Quote", "Cotización")}: <span className="text-[#C7A668] font-mono">{res.request.finalQuote}</span></p>}
          </motion.div>
        )}

        {res?.type === "ticket" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-sm border border-white/10 bg-white/[0.03] p-7">
            <div className="flex items-center justify-between"><span className="font-mono text-2xl tracking-wider text-white">{res.ticket.ticketId}</span><span className="font-mono text-[10px] text-white/40">{res.ticket.aircraftSummary}</span></div>
            <div className="mt-8 space-y-0">
              {res.stages.map((st, i) => {
                const curIdx = res.stages.indexOf(res.ticket.status);
                const done = i < curIdx, active = i === curIdx;
                return (
                  <div key={st} className="flex items-start gap-4 pb-7 last:pb-0 relative">
                    {i < res.stages.length - 1 && <span className={cx("absolute left-[11px] top-6 w-px h-full", done ? "bg-[#7DAFFF]" : "bg-white/10")} />}
                    <span className={cx("relative z-10 h-6 w-6 rounded-full border flex items-center justify-center shrink-0", done ? "bg-[#7DAFFF] border-[#7DAFFF]" : active ? "border-[#C7A668] bg-[#C7A668]/20 shadow-[0_0_15px_rgba(212,175,55,0.7)] animate-pulse" : "border-white/20")}>
                      {done ? <Check size={13} className="text-white" /> : active ? <span className="h-2 w-2 rounded-full bg-[#C7A668]" /> : null}
                    </span>
                    <span className={cx("text-sm pt-0.5", done ? "text-white/50 line-through" : active ? "text-[#C7A668] font-medium" : "text-white/30")}>{STAGE_LABELS[st][lang === "es" ? 1 : 0]}</span>
                  </div>
                );
              })}
            </div>
            {["aircraft_ready", "report_delivered"].includes(res.ticket.status) && (
              <div className="mt-6 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,.08)" }}>
                <button data-testid="view-report-btn" onClick={() => setShowReport((s) => !s)} className="font-mono text-xs uppercase tracking-widest" style={{ color: "#7DAFFF", background: "none", border: "1px solid rgba(125,175,255,.4)", borderRadius: 999, padding: "9px 18px", cursor: "pointer" }}>
                  {showReport ? L("Hide report", "Ocultar reporte") : L("View readiness report", "Ver reporte de readiness")}
                </button>
                {showReport && (
                  <motion.div data-testid="readiness-report" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,.1)" }}>
                    <div style={{ background: "linear-gradient(180deg,#0c0d10,#0a0b0d)", padding: "20px 22px", borderBottom: "1px solid rgba(255,255,255,.08)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                      <div>
                        <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".14em", color: "#7DAFFF" }}>{L("AIRCRAFT READINESS REPORT", "REPORTE DE READINESS")}</div>
                        <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{res.ticket.ticketId} · {res.ticket.aircraftSummary}</div>
                      </div>
                      <span className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "#050505", background: "#7DAFFF", borderRadius: 999, padding: "6px 14px", fontWeight: 700 }}>{L("DEPARTURE-READY", "LISTO PARA SALIR")}</span>
                    </div>
                    <div style={{ padding: "22px" }}>
                      {res.ticket.assignedSpecialist && <div style={{ fontSize: 13, color: "#B8BDC3", marginBottom: 16 }}>{L("Specialist", "Especialista")}: <span style={{ color: "#F5F5F2" }}>{res.ticket.assignedSpecialist}</span></div>}
                      <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".12em", color: "#6c727a", marginBottom: 12 }}>{L("CHECKLIST COMPLETED", "CHECKLIST COMPLETADO")}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
                        {REPORT_ITEMS.map((it, i) => (
                          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13.5, color: "#d7dadd" }}><span style={{ color: "#7DAFFF" }}>✓</span>{it[lang === "es" ? 1 : 0]}</div>
                        ))}
                      </div>
                      {res.ticket.observations && (
                        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,.08)" }}>
                          <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".12em", color: "#6c727a", marginBottom: 8 }}>{L("OBSERVATIONS", "OBSERVACIONES")}</div>
                          <p style={{ fontSize: 14, color: "#B8BDC3", lineHeight: 1.55, margin: 0 }}>{res.ticket.observations}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
